import axios from "axios";
import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import AddGraphicDesignModal from "./addGraphicDesigner";
import GraphicDesignerPreviewMokup from "./GraphicDesignerPreviewMokup";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import OrderSpecsModal, {
  parseProductImages,
} from "../../../Components/OrderSpecsModal";

const REACT_APP_IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

// Resolve a stored path (or absolute URL) into a browser-usable image URL.
const IMG = (p) =>
  !p ? "" : String(p).startsWith("http") ? p : `${REACT_APP_IMAGE_URL}${p}`;

const STATUS_STYLE = {
  Completed: { background: "var(--color-success-bg)", color: "var(--color-success-text)" },
  Assigned: { background: "var(--color-info-bg)", color: "var(--color-info-text)" },
  Pending: { background: "var(--color-warning-bg)", color: "var(--color-warning-text)" },
};

const statusOf = (item) => {
  const designs = item?.designs || [];
  if (designs.some((d) => d.status === "completed")) return "Completed";
  if (designs.length > 0) return "Assigned";
  return "Pending";
};

const GRID = {
  gridTemplateColumns: "48px 84px 2fr 72px 128px 128px 156px 104px",
};


// Clickable, overlapping thumbnail stack (matches the shared .al-imgstack design).
const ImgStack = ({ images, getSrc, onOpen, emptyLabel, title }) => {
  if (!images || images.length === 0)
    return <span className="al-muted">{emptyLabel}</span>;
  return (
    <button type="button" className="al-imgstack" title={title} onClick={onOpen}>
      {images.slice(0, 3).map((im, i) => (
        <span className="al-imgstack__i" key={i}>
          <img src={getSrc(im)} alt="" />
        </span>
      ))}
      {images.length > 3 && (
        <span className="al-imgstack__n">+{images.length - 3}</span>
      )}
    </button>
  );
};

const IconDownload = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
);

const IconEye = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
);


const DesignStudio = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [buyerId, setBuyerId] = useState(
    localStorage.getItem("graphic_designer_id")
  );
  const [orderIds, setOrderIds] = useState(localStorage.getItem("order_id"));
  const [showModal, setShowModal] = useState(false);
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [imageList, setImageList] = useState([]);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openDesc, setOpenDesc] = useState(false);
  const [activeDescription, setActiveDescription] = useState("");
  // Order whose full spec sheet is open in the modal (null = closed).
  const [specsItem, setSpecsItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Fetch Products
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/graphic_designer/order/assigned`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );
      const rawData = res?.data?.data || [];
      setProducts(Array.isArray(rawData) ? [...rawData].reverse() : rawData);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // ✅ Filter logic
  const getFilteredProducts = () => {
    if (statusFilter === "All") return products;
    return products.filter((item) => statusOf(item) === statusFilter);
  };

  const filteredProducts = getFilteredProducts();

  // Per-status counts for the filter tabs.
  const counts = products.reduce(
    (acc, item) => {
      acc.All += 1;
      acc[statusOf(item)] += 1;
      return acc;
    },
    { All: 0, Pending: 0, Assigned: 0, Completed: 0 }
  );

  const handleRowClick = (designer_id, order_id) => {
    const newId = buyerId === String(designer_id) ? null : String(designer_id);
    const newOrderId = orderIds === String(order_id) ? null : String(order_id);
    setBuyerId(newId);
    setOrderIds(newOrderId);
    newId
      ? localStorage.setItem("graphic_designer_id", newId)
      : localStorage.removeItem("graphic_designer_id");
    newOrderId
      ? localStorage.setItem("order_id", newOrderId)
      : localStorage.removeItem("order_id");
  };

  const handleOpenModal = (orderId) => {
    setOrderId(orderId);
    setShowModal(true);
  };

  const handleDesignOpenModal = (orderId) => {
    setOrderId(orderId);
    setShowDesignModal(true);
  };

  // Open the image preview modal with a plain list of resolved URLs.
  const openPreview = (urls) => {
    if (!urls || urls.length === 0) return;
    setImageList(urls);
    setSelectedImage(urls[0]);
    setShowImagesModal(true);
  };

  const handleDownloadAll = async (mediaFiles) => {
    if (!mediaFiles || mediaFiles.length === 0) return;
    const zip = new JSZip();
    const folder = zip.folder("images");
    await Promise.all(
      mediaFiles.map(async (file) => {
        try {
          const url = IMG(file.media_file);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = (url || "").split("/").pop() || "image";
          folder.file(fileName, blob);
        } catch (err) {
          console.error("Download error:", err);
        }
      })
    );
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "images.zip");
  };

  const handleProductDownloadAll = async (productImages) => {
    if (!productImages || productImages.length === 0) return;
    const zip = new JSZip();
    const folder = zip.folder("product_images");

    await Promise.all(
      productImages.map(async (img) => {
        try {
          const url = IMG(img);
          const response = await fetch(url);
          const blob = await response.blob();
          const fileName = (url || "").split("/").pop() || "product-image";
          folder.file(fileName, blob);
        } catch (err) {
          console.error("Download error:", err);
        }
      })
    );

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "product-images.zip");
  };

  const FILTERS = ["All", "Pending", "Assigned", "Completed"];

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Design Studio Orders</h1>
          <p className="al-sub">
            Orders routed to graphic designers — assign designs, preview mockups
            and track progress.
          </p>
        </div>
        <div className="al-head__actions">
          <button
            type="button"
            className="al-btn"
            onClick={fetchProducts}
            disabled={isLoading}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
            Refresh
          </button>
        </div>
      </div>

      <div className="al-card">
        {/* Status filter tabs */}
        <div className="al-toolbar" style={{ paddingBottom: 0 }}>
          <div className="al-tabs" style={{ marginBottom: 0, border: 0, flex: 1 }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`al-tab ${statusFilter === f ? "is-active" : ""}`}
                onClick={() => setStatusFilter(f)}
              >
                {f}
                <span
                  className="al-pill"
                  style={{
                    marginLeft: 7,
                    padding: "1px 7px",
                    fontSize: 10.5,
                    background: "var(--color-surface-2)",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {counts[f]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="al-tablewrap">
          <div style={{ minWidth: 1120, "--al-row-pad": "12px" }}>
            <div className="al-thead" style={GRID}>
              <span className="al-th">#</span>
              <span className="al-th">Order</span>
              <span className="al-th">Product</span>
              <span className="al-th">Specs</span>
              <span className="al-th">Product Images</span>
              <span className="al-th">User Media</span>
              <span className="al-th">Designs</span>
              <span className="al-th al-th--right">Status</span>
            </div>

            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div className="al-row" style={GRID} key={`skel-${i}`}>
                  <span className="al-skel" style={{ height: 12, width: 20 }} />
                  <span className="al-skel" style={{ height: 12, width: 44 }} />
                  <span className="al-skel" style={{ height: 12, width: "80%" }} />
                  <span className="al-skel" style={{ height: 30, width: 30 }} />
                  <span className="al-skel" style={{ height: 30, width: 90 }} />
                  <span className="al-skel" style={{ height: 30, width: 90 }} />
                  <span className="al-skel" style={{ height: 30, width: 90 }} />
                  <span className="al-skel" style={{ height: 20, width: 70, justifySelf: "end" }} />
                </div>
              ))
            ) : filteredProducts.length > 0 ? (
              filteredProducts.map((item, index) => {
                const productImages = parseProductImages(
                  item?.product?.product_image
                );

                const media = item.media_files || [];
                const designs = item.designs || [];
                const status = statusOf(item);
                const selected =
                  buyerId === String(item.graphic_designer_id) &&
                  orderIds === String(item.order_id);

                return (
                  <div
                    className="al-row"
                    style={{
                      ...GRID,
                      cursor: "pointer",
                      background: selected ? "var(--color-primary-weak)" : undefined,
                      boxShadow: selected
                        ? "inset 3px 0 0 var(--color-primary)"
                        : undefined,
                    }}
                    key={item.order_id}
                    onClick={() =>
                      handleRowClick(item.graphic_designer_id, item.order_id)
                    }
                  >
                    <span className="al-mono al-muted">{index + 1}</span>
                    <span className="al-mono" style={{ fontWeight: 700 }}>
                      #{item.order_id}
                    </span>

                    {/* Product title + description */}
                    <div style={{ minWidth: 0 }}>
                      <div className="al-name">{item.title || "—"}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                        <span
                          className="al-muted"
                          style={{
                            fontSize: 12,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 220,
                          }}
                        >
                          {item.product_description || "No description"}
                        </span>
                        {item.product_description && (
                          <button
                            type="button"
                            className="al-act"
                            title="View full description"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDescription(item.product_description);
                              setOpenDesc(true);
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Specs — full sheet opens in a modal */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="al-act al-act--primary"
                        title="View full product specs"
                        onClick={() => setSpecsItem(item)}
                      >
                        <IconEye />
                      </button>
                    </div>

                    {/* Product Images */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                      <ImgStack
                        images={productImages}
                        getSrc={IMG}
                        title="Preview product images"
                        emptyLabel="No image"
                        onOpen={() => openPreview(productImages.map(IMG))}
                      />
                      {productImages.length > 0 && (
                        <button type="button" className="al-act" title="Download all"
                          onClick={() => handleProductDownloadAll(productImages)}>
                          <IconDownload />
                        </button>
                      )}
                    </div>

                    {/* User Media */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                      <ImgStack
                        images={media}
                        getSrc={(m) => IMG(m.media_file)}
                        title="Preview user media"
                        emptyLabel="No media"
                        onOpen={() => openPreview(media.map((m) => IMG(m.media_file)))}
                      />
                      {media.length > 0 && (
                        <button type="button" className="al-act" title="Download all"
                          onClick={() => handleDownloadAll(media)}>
                          <IconDownload />
                        </button>
                      )}
                    </div>

                    {/* Designs */}
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
                      <ImgStack
                        images={designs}
                        getSrc={(d) => IMG(d.design_url)}
                        title="Preview designs"
                        emptyLabel="No designs"
                        onOpen={() => handleDesignOpenModal(item.order_id)}
                      />
                      <button type="button" className="al-act al-act--primary" title="Assign / add design"
                        onClick={() => handleOpenModal(item.order_id)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      </button>
                    </div>

                    {/* Status */}
                    <span style={{ justifySelf: "end" }}>
                      <span className="al-pill" style={STATUS_STYLE[status]}>
                        {status}
                      </span>
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="al-empty">
                <div className="al-empty__icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                </div>
                <p className="al-empty__title">No Orders Found</p>
                <p className="al-empty__text">
                  {statusFilter === "All"
                    ? "No design studio orders have been assigned yet."
                    : `No ${statusFilter.toLowerCase()} orders right now.`}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="al-foot">
          <span className="al-foot__info">
            Showing <strong>{filteredProducts.length}</strong>{" "}
            {filteredProducts.length === 1 ? "order" : "orders"}
          </span>
        </div>
      </div>

      {/*
        Full product specs (shared across Design Studio, Inbound, Buyer list).
        No `buyer` prop: a designer works from the brief, and the buyer's
        identity is not theirs to see. The API no longer sends it either.
      */}
      <OrderSpecsModal
        open={!!specsItem}
        onClose={() => setSpecsItem(null)}
        order={specsItem}
        product={specsItem?.product}
        extraFields={[
          { label: "Design Status", value: specsItem ? statusOf(specsItem) : null },
          { label: "Designs Uploaded", value: (specsItem?.designs || []).length },
          { label: "User Media", value: (specsItem?.media_files || []).length },
        ]}
      />

      {/* Description Modal */}
      {openDesc && (
        <div className="al-modal" onClick={() => setOpenDesc(false)}>
          <div className="al-modal__scrim" />
          <div className="al-modal__box" onClick={(e) => e.stopPropagation()} style={{ width: "min(520px,92vw)" }}>
            <div className="al-modal__head">
              <h3 className="al-modal__title">Full Product Description</h3>
              <button type="button" className="al-act" onClick={() => setOpenDesc(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: 20, fontSize: 14, color: "var(--color-text-secondary)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6, maxHeight: "60vh", overflowY: "auto" }}>
              {activeDescription || "No description available"}
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImagesModal && (
        <div className="al-modal" onClick={() => setShowImagesModal(false)}>
          <div className="al-modal__scrim" />
          <div className="al-modal__box" onClick={(e) => e.stopPropagation()} style={{ width: "min(560px,94vw)" }}>
            <div className="al-modal__head">
              <h3 className="al-modal__title">Image Preview</h3>
              <button type="button" className="al-act" onClick={() => setShowImagesModal(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                {selectedImage ? (
                  <img src={selectedImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: 340, borderRadius: 12, objectFit: "contain" }} />
                ) : (
                  <p className="al-muted">No image available</p>
                )}
              </div>
              {imageList.length > 1 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                  {imageList.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedImage(url)}
                      className="al-thumb"
                      style={{
                        width: 48,
                        height: 48,
                        cursor: "pointer",
                        outline: selectedImage === url ? "2px solid var(--color-primary)" : "none",
                      }}
                    >
                      <img src={url} alt="" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Other Modals */}
      {showModal && (
        <AddGraphicDesignModal
          show={showModal}
          setShow={setShowModal}
          fetchProducts={fetchProducts}
          orderId={orderId}
          onClose={() => setShowModal(false)}
        />
      )}
      {showDesignModal && (
        <GraphicDesignerPreviewMokup
          show={showDesignModal}
          userId={user.user_id}
          setShow={setShowDesignModal}
          orderId={orderId}
          onClose={() => setShowDesignModal(false)}
        />
      )}
    </div>
  );
};

export default DesignStudio;
